import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DraftNotice, useFormDraft } from '../nav/drafts';
import { leaveWithoutAsking, sameValues, useUnsavedGuard } from '../ui/unsaved';
import { formatDecimal, parseScaled } from '../ui/numbers';
import { NumberInput } from '../ui/NumberInput';
import { DialogLayer } from '../ui/Dialog';
import { CONTRACTOR_COLORS, markStyle } from './colors';

export type Kind = 'client' | 'supplier' | 'both';
export const KIND_LABELS: Record<Kind, string> = { client: 'Klient', supplier: 'Dostawca', both: 'Klient i dostawca' };

/** As the API sends it. A field the user may not see is simply absent. */
export type Contractor = {
  id: string;
  kind: Kind;
  name: string;
  nip?: string | null;
  /** Owner 2026-11-03: 9 or 14 digits; „Pobierz z GUS” fills it. */
  regon?: string | null;
  address?: string;
  email?: string;
  phone?: string;
  notes?: string;
  default_discount_bp?: number;
  /** F34: payment term of invoices in days; null = the invoices settings' default. */
  payment_days?: number | null;
  /** Owner 2026-10-23 20:49: a palette key (contractors/colors.tsx), null: none. */
  color?: string | null;
  version: number;
};

export const CONTRACTORS_KEY = ['contractors'] as const;

/** "5,5" (percent typed by a person) -> 550 basis points. Empty means 0; null means "not a valid discount". */
export function percentToBp(text: string): number | null {
  if (text.trim() === '') return 0;
  const bp = parseScaled(text, 2, { maxIntegerDigits: 3 });
  return bp === null || bp > 10_000 ? null : bp;
}

/** 550 -> "5,5" */
export function bpToPercent(bp: number): string {
  return formatDecimal(bp / 100);
}

function FieldError({ error, path }: { error: unknown; path: string }) {
  if (!(error instanceof ApiError)) return null;
  const issue = fieldIssues(error).find((i) => i.path === path);
  // A duplicate NIP comes back as 422 with the Polish message naming the existing contractor.
  const message = issue?.message ?? (path === 'nip' && error.status === 422 ? error.message : null);
  return message ? <span className="field-error">{message}</span> : null;
}

function GeneralError({ error }: { error: unknown }) {
  if (!(error instanceof ApiError) || error.status === 409 || error.status === 422) return null;
  if (error.error === 'validation' && fieldIssues(error).length > 0) return null;
  return (
    <p className="alert" role="alert">
      {error.message}
    </p>
  );
}

const draftOf = (c?: Contractor) => ({
  kind: (c?.kind ?? 'client') as Kind,
  name: c?.name ?? '',
  nip: c?.nip ?? '',
  regon: c?.regon ?? '',
  address: c?.address ?? '',
  email: c?.email ?? '',
  phone: c?.phone ?? '',
  notes: c?.notes ?? '',
  discount: bpToPercent(c?.default_discount_bp ?? 0),
  paymentDays: c?.payment_days == null ? '' : String(c.payment_days),
  color: c?.color ?? '',
});

/** "30" → 30, "" → null (the default of the invoices settings); anything else is sent as is for the server's Polish message. */
const daysOf = (text: string): number | null => (text.trim() === '' ? null : /^\d+$/.test(text.trim()) ? Number(text.trim()) : (NaN as number));

/** What „Pobierz z GUS” answers (public data of the REGON register). */
type GusCompany = { name: string; regon: string; address: string };

/**
 * „Pobierz z GUS” next to the NIP (owner 2026-11-03): asks the server for the company of the typed NIP and hands its
 * name, address and REGON to the form (which fills them — replacing what was there — and saves nothing). Errors in
 * Polish under the NIP; „Brak klucza GUS” links to Ustawienia only for the owner (only he can enter the key).
 */
function GusLookup({ nip, onFound }: { nip: string; onFound: (company: GusCompany) => void }) {
  const { me } = useMe();
  const [missingNip, setMissingNip] = useState(false);
  const lookup = useMutation({
    mutationFn: () => api<GusCompany>('/api/gus/lookup', { method: 'POST', body: { nip } }),
    onSuccess: onFound,
  });
  // review M6: a changed NIP clears the message of the previous one
  const { reset } = lookup;
  useEffect(() => {
    reset();
    setMissingNip(false);
  }, [nip, reset]);
  const error = lookup.error;
  const message = error instanceof ApiError ? (fieldIssues(error)[0]?.message ?? error.message) : error ? 'Nie udało się pobrać danych z GUS' : null;
  const noKey = error instanceof ApiError && error.code === 'gus_no_key';
  return (
    <div className="con-gus" data-feature-id="gus.lookup">
      <button
        className="link-button"
        type="button"
        disabled={lookup.isPending}
        onClick={() => {
          lookup.reset();
          setMissingNip(nip.trim() === '');
          if (nip.trim() !== '') lookup.mutate();
        }}
      >
        {lookup.isPending ? 'Pobieranie z GUS…' : 'Pobierz z GUS'}
      </button>
      {missingNip && <span className="field-error">Wpisz NIP, żeby pobrać dane z GUS.</span>}
      {message &&
        (noKey ? (
          <span className="field-error" role="alert">
            {me?.isOwner ? (
              <>
                Brak klucza GUS – wpisz go w <Link to="/ustawienia">Ustawieniach</Link>
              </>
            ) : (
              'Brak klucza GUS'
            )}
          </span>
        ) : (
          <span className="field-error" role="alert">
            {message}
          </span>
        ))}
    </div>
  );
}

/**
 * „Usunąć kontrahenta?” (owner 2026-10-23 19:27: „guzik usun przenies do edytuj. nie zostawiaj go na wierzchu”): opened
 * from the edit form (or, for a role that may delete but not edit, from the row's „…”, review I2). The rules stay the server's: a contractor still used (events, invoices, subrentals…) is refused
 * with 422 `contractor_in_use` naming the uses; a stale version is a 409.
 */
export function DeleteDialog({ contractor, onClose, onDeleted }: { contractor: Contractor; onClose: () => void; onDeleted: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/api/contractors/${contractor.id}?version=${contractor.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CONTRACTORS_KEY });
      onDeleted();
    },
  });
  const conflict = remove.error instanceof ApiError && remove.error.status === 409;
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="delete-title">
        <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="delete-title">Usunąć kontrahenta?</h2>
        <p>„{contractor.name}” zniknie z listy. Kontrahent zostaje w bazie (administrator może go przywrócić), a w historii zmian zostaje wpis o usunięciu.</p>
        {conflict && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: CONTRACTORS_KEY });
              onClose();
            }}
          />
        )}
        {remove.error && !conflict && (
          <p className="alert" role="alert">
            {remove.error instanceof ApiError ? remove.error.message : 'Nie udało się usunąć'}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : 'Usuń'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Add (no `contractor`) or edit. `canDelete`: „Usuń kontrahenta” at the bottom left of the edit form. `visible(field)`: the server sent that field, so the user may see and set it.
 * A hidden field is neither shown nor sent. On edit only changed fields go out, so a field the user cannot see is never overwritten.
 */
export function ContractorForm({ contractor, visible, onDone, canDelete = false }: { contractor?: Contractor; visible: (field: string) => boolean; onDone: () => void; canDelete?: boolean }) {
  const [deleting, setDeleting] = useState(false);
  const queryClient = useQueryClient();
  const [base, setBase] = useState(contractor);
  // P13: what was typed waits for the way back
  const kept = useFormDraft<ReturnType<typeof draftOf>>(`contractors.contractor:${contractor?.id ?? 'new'}`, base?.version ?? 0);
  const [draft, setDraft] = useState(() => kept.initial ?? draftOf(contractor));
  kept.track(draft);
  /** Saved or cancelled: the draft is no longer wanted. */
  const finish = () => {
    kept.clear();
    onDone();
  };
  const [discountError, setDiscountError] = useState('');
  const [daysError, setDaysError] = useState('');
  const showDiscount = visible('default_discount_bp');
  const set = (patch: Partial<typeof draft>) => setDraft({ ...draft, ...patch });

  const save = useMutation({
    mutationFn: () => {
      const bp = percentToBp(draft.discount);
      const original = draftOf(base);
      const wanted = (key: keyof typeof draft, field: string) => visible(field) && (!base || draft[key] !== original[key]);
      const fields = {
        // `kind` is required when adding; it is sent even if hidden (the server then answers 403, see the report).
        ...(wanted('kind', 'kind') || !base ? { kind: draft.kind } : {}),
        ...(wanted('name', 'name') ? { name: draft.name } : {}),
        ...(wanted('nip', 'nip') ? { nip: draft.nip } : {}),
        ...(wanted('regon', 'regon') ? { regon: draft.regon } : {}),
        ...(wanted('address', 'address') ? { address: draft.address } : {}),
        ...(wanted('email', 'email') ? { email: draft.email } : {}),
        ...(wanted('phone', 'phone') ? { phone: draft.phone } : {}),
        ...(wanted('notes', 'notes') ? { notes: draft.notes } : {}),
        ...(wanted('discount', 'default_discount_bp') && bp !== null ? { default_discount_bp: bp } : {}),
        ...(wanted('paymentDays', 'payment_days') ? { payment_days: daysOf(draft.paymentDays) } : {}),
        ...(wanted('color', 'color') ? { color: draft.color === '' ? null : draft.color } : {}),
      };
      return base
        ? api<Contractor>(`/api/contractors/${base.id}`, { method: 'PATCH', body: { version: base.version, ...fields } })
        : api<Contractor>('/api/contractors', { method: 'POST', body: fields });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CONTRACTORS_KEY });
      finish();
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;

  async function reload() {
    // Take the server's current state as the new baseline; the person re-applies their change.
    const current = (save.error as ApiError).current as Contractor | undefined;
    const fresh = current ?? (await api<Contractor>(`/api/contractors/${base!.id}`));
    setBase(fresh);
    setDraft(draftOf(fresh));
    save.reset();
    await queryClient.invalidateQueries({ queryKey: CONTRACTORS_KEY });
  }

  /** The fields are right to send (else they say why). */
  function checked(): boolean {
    if (showDiscount && percentToBp(draft.discount) === null) {
      setDiscountError('Podaj rabat od 0 do 100% (najwyżej dwa miejsca po przecinku).');
      return false;
    }
    setDiscountError('');
    if (visible('payment_days') && Number.isNaN(daysOf(draft.paymentDays))) {
      setDaysError('Podaj liczbę dni (np. 14) albo zostaw puste.');
      return false;
    }
    setDaysError('');
    return true;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (checked()) save.mutate();
  }

  // owner 2026-10-23 13:34: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(draft, draftOf(base)),
    save: async () => {
      if (!checked()) return false;
      await save.mutateAsync();
      return true;
    },
    discard: () => kept.clear(),
  });

  return (
    <form
      ref={guardRef}
      className={base ? 'edit' : 'panel form'}
      aria-label={base ? `Edycja: ${base.name}` : 'Nowy kontrahent'}
      data-feature-id="contractors.edit"
      onSubmit={submit}
    >
      {!base && <h2>Nowy kontrahent</h2>}
      <DraftNotice keeper={kept} onRestore={setDraft} />
      {conflict && <ConflictNotice onReload={() => void reload()} />}
      <GeneralError error={save.error} />
      {visible('name') && (
        <label className="field">
          <span>Nazwa</span>
          <input value={draft.name} onChange={(e) => set({ name: e.target.value })} required autoComplete="off" />
          <FieldError error={save.error} path="name" />
        </label>
      )}
      {visible('kind') && (
        <label className="field">
          <span>Rodzaj</span>
          <select value={draft.kind} onChange={(e) => set({ kind: e.target.value as Kind })}>
            {(Object.keys(KIND_LABELS) as Kind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
          <FieldError error={save.error} path="kind" />
        </label>
      )}
      {visible('nip') && (
        <label className="field">
          <span>NIP</span>
          <input value={draft.nip} onChange={(e) => set({ nip: e.target.value })} inputMode="numeric" data-no-select-all="" autoComplete="off" />
          <FieldError error={save.error} path="nip" />
        </label>
      )}
      {visible('nip') && (
        <GusLookup
          nip={draft.nip}
          onFound={(company) =>
            // owner 2026-11-03: the click asks for it, so the register's values replace what was typed (visible fields only)
            setDraft((d) => ({
              ...d,
              ...(visible('name') ? { name: company.name } : {}),
              ...(visible('address') ? { address: company.address } : {}),
              ...(visible('regon') ? { regon: company.regon } : {}),
            }))
          }
        />
      )}
      {visible('regon') && (
        <label className="field">
          <span>REGON</span>
          <input value={draft.regon} onChange={(e) => set({ regon: e.target.value })} inputMode="numeric" data-no-select-all="" autoComplete="off" />
          <FieldError error={save.error} path="regon" />
        </label>
      )}
      {visible('phone') && (
        <label className="field">
          <span>Telefon</span>
          <input type="tel" inputMode="tel" value={draft.phone} onChange={(e) => set({ phone: e.target.value })} autoComplete="off" />
          <FieldError error={save.error} path="phone" />
        </label>
      )}
      {visible('email') && (
        <label className="field">
          <span>E-mail</span>
          <input type="email" inputMode="email" autoCapitalize="none" value={draft.email} onChange={(e) => set({ email: e.target.value })} autoComplete="off" />
          <FieldError error={save.error} path="email" />
        </label>
      )}
      {visible('address') && (
        <label className="field">
          <span>Adres</span>
          <textarea rows={2} value={draft.address} onChange={(e) => set({ address: e.target.value })} />
          <FieldError error={save.error} path="address" />
        </label>
      )}
      {showDiscount && (
        <label className="field">
          <span>Domyślny rabat (%)</span>
          <NumberInput value={draft.discount} onValueChange={(v) => set({ discount: v })} />
          {discountError && <span className="field-error">{discountError}</span>}
          <FieldError error={save.error} path="default_discount_bp" />
        </label>
      )}
      {visible('payment_days') && (
        <label className="field">
          <span>Termin płatności (dni)</span>
          <input value={draft.paymentDays} onChange={(e) => set({ paymentDays: e.target.value })} inputMode="numeric" placeholder="domyślny z ustawień faktur" autoComplete="off" />
          {daysError && <span className="field-error">{daysError}</span>}
          <FieldError error={save.error} path="payment_days" />
        </label>
      )}
      {visible('color') && (
        // owner 2026-10-23 20:49: radios — Tab into the row, arrows between the swatches, the chosen one has a ring
        <fieldset className="field con-colors" data-feature-id="contractors.color">
          <legend>Kolor</legend>
          <div className="con-swatches" role="radiogroup" aria-label="Kolor">
            <label className="con-swatch con-swatch-none" title="Brak koloru">
              <input type="radio" name="contractor-color" value="" checked={draft.color === ''} onChange={() => set({ color: '' })} />
              <span>Brak</span>
            </label>
            {CONTRACTOR_COLORS.map((c) => (
              <label key={c.key} className="con-swatch" title={c.label} style={markStyle(c.key)}>
                <input type="radio" name="contractor-color" value={c.key} checked={draft.color === c.key} onChange={() => set({ color: c.key })} aria-label={c.label} />
              </label>
            ))}
          </div>
          <FieldError error={save.error} path="color" />
        </fieldset>
      )}
      {visible('notes') && (
        <label className="field">
          <span>Uwagi</span>
          <textarea rows={3} value={draft.notes} onChange={(e) => set({ notes: e.target.value })} />
          <FieldError error={save.error} path="notes" />
        </label>
      )}
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj kontrahenta'}
        </button>
        <button className="btn" type="button" onClick={() => leaveWithoutAsking(finish)}>
          Anuluj
        </button>
      </div>
      {base && canDelete && (
        <div className="con-form-foot">
          <button className="btn small ghost danger" type="button" aria-label={`Usuń: ${base.name}`} onClick={() => setDeleting(true)}>
            Usuń kontrahenta
          </button>
        </div>
      )}
      {deleting && base && <DeleteDialog contractor={base} onClose={() => setDeleting(false)} onDeleted={() => leaveWithoutAsking(finish)} />}
    </form>
  );
}
